import React, { useState } from 'react';
import Select from 'react-select';
import { getDisplayValue } from '../../../utils/displayValue';

const UniversityFields = ({
    formData,
    handleChange,
    countries,
    cities,
    universities,
    majors,
    setFormData
}) => {
    // State to track focus for each field
    const [isFocused, setIsFocused] = useState({
        country: false,
        programme: false,
        city: false,
        university: false,
        major: false,
    });

    // State to track if a field has been touched (blurred)
    const [isTouched, setIsTouched] = useState({
        country: false,
        programme: false,
        city: false,
        university: false,
        major: false,
    });

    // Helper to check if a field actually has a value
    const fieldHasValue = (fieldValue) => {
        // If it's an array (e.g. major), check length
        if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0;
        }
        // Otherwise, just do a boolean check
        return !!fieldValue;
    };

    // Handle focus event
    const handleFocus = (field) => {
        setIsFocused(prev => ({ ...prev, [field]: true }));
    };

    // Handle blur event: mark the field as not focused and as touched
    const handleBlur = (field) => {
        setIsFocused(prev => ({ ...prev, [field]: false }));
        setIsTouched(prev => ({ ...prev, [field]: true }));
    };

    // Determine label styles based on field state
    const getLabelStyles = (field, value) => {
        const hasVal = fieldHasValue(value);

        if (isFocused[field]) {
            // Field is currently focused
            return {
                color: '#22c55e', // green
                top: '-10px',
                fontSize: '12px',
                zIndex: 20,
            };
        } else if (isTouched[field] && !hasVal) {
            // Field was touched (blurred) but left empty
            return {
                color: '#ef4444', // red
                top: '8px',
                fontSize: '14px',
                zIndex: 20,
            };
        } else {
            // Default or valid state
            return {
                color: '#6b7280', // gray
                top: hasVal ? '-10px' : '8px',
                fontSize: hasVal ? '12px' : '14px',
                zIndex: 20,
            };
        }
    };

    // Determine border color based on field state
    const getBorderColor = (field, value) => {
        const hasVal = fieldHasValue(value);

        if (isFocused[field]) {
            return '#22c55e'; // green
        } else if (isTouched[field] && !hasVal) {
            return '#ef4444'; // red
        } else {
            return '#6b7280'; // gray
        }
    };

    // Determine validation message based on field state
    const getValidationMessage = (field, value) => {
        const hasVal = fieldHasValue(value);

        return isTouched[field] && !hasVal
            ? `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
            : `Please select ${field.charAt(0).toUpperCase() + field.slice(1)}`;
    };

    // Common react-select style overrides
    const commonSelectStyles = (field) => ({
        placeholder: '',  // remove default "Select..."
        styles: {
            control: (base, state) => ({
                ...base,
                minHeight: '42px',
                borderColor: getBorderColor(field, formData[field]),
                '&:hover': {
                    borderColor: getBorderColor(field, formData[field]),
                },
                '&:focus-within': {
                    borderColor: '#22c55e',
                    boxShadow: '0 0 0 #22c55e'
                },
                backgroundColor: state.isDisabled ? 'white' : base.backgroundColor,
                zIndex: 10,
            }),
            menu: (base) => ({
                ...base,
                zIndex: 30,
            }),
            option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                    ? 'white'
                    : state.isFocused ? '#3265CB' : base.backgroundColor,
                color: state.isSelected
                    ? '#374151'
                    : state.isFocused ? 'white' : base.color,
                '&:active': {
                    backgroundColor: '#3265CB',
                    color: 'white'
                }
            }),
            // Hide the vertical separator
            indicatorSeparator: () => ({
                display: 'none'
            })
        }
    });

    return (
        <div>
            <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">
                Area of Interest *
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* Country */}
                <div className="relative">
                    <span
                        style={getLabelStyles('country', formData.country)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        Country *
                    </span>
                    <Select
                        id="country"
                        name="country"
                        options={countries.map(country => ({
                            value: getDisplayValue(country, ''),
                            label: getDisplayValue(country, '')
                        }))}
                        value={
                            formData.country
                                ? {
                                      value: getDisplayValue(formData.country, ''),
                                      label: getDisplayValue(formData.country, '')
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            handleChange({
                                target: {
                                    name: 'country',
                                    value: selected ? selected.value : ''
                                }
                            });
                        }}
                        onFocus={() => handleFocus('country')}
                        onBlur={() => handleBlur('country')}
                        {...commonSelectStyles('country')}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.country && !formData.country
                                    ? '#ef4444'
                                    : '#6b7280'
                        }}
                    >
                        {getValidationMessage('country', formData.country)}
                    </span>
                </div>

                {/* Programme */}
                <div className="relative">
                    <span
                        style={getLabelStyles('programme', formData.programme)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        Programme *
                    </span>
                    <Select
                        id="programme"
                        name="programme"
                        options={
                            formData.country
                                ? ['Bachelors', 'Masters'].map(program => ({
                                      value: program,
                                      label: program
                                  }))
                                : []
                        }
                        value={
                            formData.programme
                                ? {
                                      value: formData.programme,
                                      label: formData.programme
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            handleChange({
                                target: {
                                    name: 'programme',
                                    value: selected ? selected.value : ''
                                }
                            });
                        }}
                        onFocus={() => handleFocus('programme')}
                        onBlur={() => handleBlur('programme')}
                        isDisabled={!formData.country}
                        {...commonSelectStyles('programme')}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.programme && !formData.programme
                                    ? '#ef4444'
                                    : '#6b7280'
                        }}
                    >
                        {getValidationMessage('programme', formData.programme)}
                    </span>
                </div>

                {/* City */}
                <div className="relative">
                    <span
                        style={getLabelStyles('city', formData.city)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        City *
                    </span>
                    <Select
                        id="city"
                        name="city"
                        options={cities.map(city => ({
                            value: getDisplayValue(city, ''),
                            label: getDisplayValue(city, '')
                        }))}
                        value={
                            formData.city
                                ? {
                                      value: getDisplayValue(formData.city, ''),
                                      label: getDisplayValue(formData.city, '')
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            handleChange({
                                target: {
                                    name: 'city',
                                    value: selected ? selected.value : ''
                                }
                            });
                        }}
                        onFocus={() => handleFocus('city')}
                        onBlur={() => handleBlur('city')}
                        isDisabled={!formData.programme}
                        {...commonSelectStyles('city')}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.city && !formData.city
                                    ? '#ef4444'
                                    : '#6b7280'
                        }}
                    >
                        {getValidationMessage('city', formData.city)}
                    </span>
                </div>

                {/* University */}
                <div className="relative">
                    <span
                        style={getLabelStyles('university', formData.university)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        University *
                    </span>
                    <Select
                        id="university"
                        name="university"
                        options={universities.map(university => ({
                            value: getDisplayValue(university, ''),
                            label: getDisplayValue(university, '')
                        }))}
                        value={
                            formData.university
                                ? {
                                      value: getDisplayValue(formData.university, ''),
                                      label: getDisplayValue(formData.university, '')
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            handleChange({
                                target: {
                                    name: 'university',
                                    value: selected ? selected.value : ''
                                }
                            });
                        }}
                        onFocus={() => handleFocus('university')}
                        onBlur={() => handleBlur('university')}
                        isDisabled={!formData.city}
                        {...commonSelectStyles('university')}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.university && !formData.university
                                    ? '#ef4444'
                                    : '#6b7280'
                        }}
                    >
                        {getValidationMessage('university', formData.university)}
                    </span>
                </div>

                {/* Major (Single-select) */}
                <div className="relative">
                    <span
                        style={getLabelStyles('major', formData.major)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        Major *
                    </span>
                    <Select
                        id="major"
                        name="major"
                        options={majors.map(major => ({
                            value: getDisplayValue(major, ''),
                            label: getDisplayValue(major, '')
                        }))}
                        value={
                            formData.major
                                ? {
                                      value: getDisplayValue(formData.major, ''),
                                      label: getDisplayValue(formData.major, '')
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            handleChange({
                                target: {
                                    name: 'major',
                                    value: selected ? selected.value : ''
                                }
                            });
                        }}
                        onFocus={() => handleFocus('major')}
                        onBlur={() => handleBlur('major')}
                        isDisabled={!formData.university}
                        {...commonSelectStyles('major')}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.major && !formData.major
                                    ? '#ef4444'
                                    : '#6b7280'
                        }}
                    >
                        {getValidationMessage('major', formData.major)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default UniversityFields;
